import React, {
    useState,
    useRef,
    useEffect,
    useCallback,
    type SyntheticEvent,
    createRef
} from "react";

import { css } from "@compiled/react";
// @ts-ignore
import { useInView } from "react-intersection-observer";
import { useIntl } from "react-intl";

import ChevronLeftLargeIcon from "@atlaskit/icon/glyph/chevron-left-large";
import ChevronRightLargeIcon from "@atlaskit/icon/glyph/chevron-right-large";
import { media } from "@atlaskit/primitives/responsive";

import { Adg4RaisedMedia } from "@/components/adg4/raised-media/src";
import { wacToken } from "@/libraries/design/foundations";

import { ArrowButton, PlayPauseButton } from "./carouselButtons.tsx";

export interface VideoSlide {
    primaryText: string;
    description: string;
    videoSrcMp4: string;
    videoSrcWebm: string;
    videoPreviewSrc: string;
}

interface VideoCarouselSlideProps extends VideoSlide {
    id: string;
    loop: boolean;
    onEnded: () => void;
}

const carouselSlideStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: "16px"
});

const infoContainerStyle = css({
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "16px",
    minHeight: "100px"
});

const textContainerStyle = css({
    display: "flex",
    flexDirection: "column",
    width: "100%",
    textAlign: "center"
});

const primaryTextStyle = css({
    fontWeight: "700 !important",
    fontSize: "24px !important",
    lineHeight: "30px !important",
    letterSpacing: "0.3px !important",
    marginBlockEnd: "6px",
    fontFamily: `${wacToken("font.charlie.display")} !important`,
    [media.below.md]: {
        fontSize: "22px !important"
    }
});

const subTextStyle = css({
    fontWeight: "400",
    fontSize: "16px",
    lineHeight: "24px"
});

const videoStyle = css({
    maxWidth: "100%",
    height: "auto"
});

const rightAlignedControlsContainerStyles = css({
    gridArea: "right"
});

export const VideoCarouselSlide: React.FC<VideoCarouselSlideProps> = ({
    primaryText,
    description,
    videoSrcMp4,
    videoSrcWebm,
    videoPreviewSrc,
    id,
    loop,
    onEnded
}) => {
    const { ref: containerRef, inView: sectionIsVisible } = useInView({
        initialInView: true,
        threshold: 0.3
    });
    const videoRef = createRef<HTMLVideoElement>();

    useEffect(() => {
        const videoElem = videoRef.current;
        if (!videoElem) return;

        // Pause the video when it's out of view
        if (!sectionIsVisible && !videoElem.paused) {
            videoElem.pause();
        }

        // Play the video when it's in view
        if (sectionIsVisible && videoElem.paused) {
            videoElem.currentTime = 0;
            videoElem.play().catch(() => {
                // Ignore error likely due to autoplay restrictions
            });
        }
    }, [videoRef, sectionIsVisible]);

    return (
        <div
            role="tabpanel"
            id={id}
            css={carouselSlideStyle}
            ref={containerRef}
        >
            <Adg4RaisedMedia>
                <video
                    width={1920}
                    height={1080}
                    css={videoStyle}
                    playsInline={true}
                    muted={true}
                    preload="auto"
                    poster={videoPreviewSrc}
                    autoPlay={true}
                    loop={loop}
                    ref={videoRef}
                    onEnded={onEnded}
                    // Use onLoadedMetadata to trigger a Safari autplay workaround
                    onLoadedMetadata={(
                        videoEvent: SyntheticEvent<HTMLVideoElement, Event>
                    ) => {
                        try {
                            videoEvent.currentTarget.currentTime = 0; // Set currentTime to workaround Safari not autplaying
                            if (videoEvent.currentTarget.parentElement) {
                                videoEvent.currentTarget
                                    .play()
                                    .catch(() => false); // Call play() as backup to ensure the video is playing
                            }
                        } catch (error) {
                            console.log(
                                "Error calling play method on video after onCanPlay is invoked."
                            );
                        }
                    }}
                >
                    <source src={videoSrcMp4} type={`video/mp4`} />
                    <source src={videoSrcWebm} type={`video/webm`} />
                </video>
            </Adg4RaisedMedia>
            <div css={infoContainerStyle}>
                <div css={textContainerStyle}>
                    <h4 css={primaryTextStyle}>{primaryText}</h4>
                    <span css={subTextStyle}>{description}</span>
                </div>
            </div>
        </div>
    );
};

const carouselTabListStyle = css({
    display: "flex",
    gap: "24px"
});

const dotStyle = css({
    borderRadius: "50%",
    cursor: "pointer",
    width: "12px",
    height: "12px",
    backgroundColor: wacToken("color.neutral.light0")
});

const currentDotStyle = css({
    // eslint-disable-next-line local/ensure-wac-token-usage
    backgroundColor: "#0052CC"
});

const carouselStyles = css({
    display: "flex",
    flexDirection: "column",
    alignItems: "center"
});

const controlsContainerStyles = css({
    display: "grid",
    gridTemplateAreas: '". center right"',
    gridTemplateColumns: "1fr auto 1fr",
    gap: "min(56px, 10%)",
    marginBlockStart: "12px"
});

const centeredControlsContainerStyles = css({
    gridArea: "center",
    display: "flex",
    alignItems: "center",
    gap: "16px"
});

export interface VideoCarouselProps {
    "aria-label": string;
    "slides": Array<VideoSlide>;
    "id": string;
}

export const VideoCarousel: React.FC<VideoCarouselProps> = ({
    "aria-label": ariaLabel,
    slides,
    "id": carouselId
}) => {
    const intl = useIntl();
    const tabsRef = useRef<Record<number, HTMLDivElement>>({});
    const [visibleSlideIndex, _setVisibleSlideIndex] = useState(0);
    const visibleSlide = slides[visibleSlideIndex];
    if (visibleSlide === undefined) {
        throw new Error("Slide index out of bounds.");
    }
    const setVisibleSlideIndex = (
        newState: React.SetStateAction<number>,
        moveFocus = false
    ) => {
        _setVisibleSlideIndex((prevState) => {
            let calculatedState;
            if (typeof newState === "function") {
                calculatedState = newState(prevState);
            } else {
                calculatedState = newState;
            }
            moveFocus && tabsRef?.current[calculatedState]?.focus();
            return calculatedState;
        });
    };

    const moveToPreviousSlide = (moveFocus = false) => {
        setVisibleSlideIndex((prev) => {
            return prev === 0 ? slides.length - 1 : prev - 1;
        }, moveFocus);
    };

    const moveToNextSlide = useCallback(
        (moveFocus = false) => {
            setVisibleSlideIndex((prev) => {
                return prev === slides.length - 1 ? 0 : prev + 1;
            }, moveFocus);
        },
        [slides.length]
    );

    const [isRotating, setIsRotating] = useState(true);

    useEffect(() => setIsRotating(true), [moveToNextSlide]);

    const handleKeyDown = (e: React.KeyboardEvent) => {
        switch (e.key) {
            case "ArrowRight":
                moveToNextSlide(true);
                break;
            case "ArrowLeft":
                moveToPreviousSlide(true);
                break;
            case "Home":
                setVisibleSlideIndex(0, true);
                break;
            case "End":
                setVisibleSlideIndex(slides.length - 1, true);
                break;
            default:
                return;
        }
        setIsRotating(false);
        e.preventDefault();
    };

    const handleTabClick = (index: number) => {
        setIsRotating(false);
        setVisibleSlideIndex(index);
    };

    const handlePreviousClick = () => {
        setIsRotating(false);
        moveToPreviousSlide();
    };

    const handleNextClick = () => {
        setIsRotating(false);
        moveToNextSlide();
    };

    const getSlideId = (suffix: string | number) => `${carouselId}-${suffix}`;

    return (
        <section
            aria-label={ariaLabel}
            aria-roledescription="carousel"
            css={carouselStyles}
        >
            <div aria-atomic={false} aria-live={isRotating ? "off" : "polite"}>
                <VideoCarouselSlide
                    key={visibleSlide.primaryText}
                    {...visibleSlide}
                    id={getSlideId(visibleSlideIndex)}
                    loop={!isRotating}
                    onEnded={() => moveToNextSlide()}
                />
            </div>
            <div css={controlsContainerStyles}>
                <div css={centeredControlsContainerStyles}>
                    <ArrowButton
                        onClick={handlePreviousClick}
                        icon={<ChevronLeftLargeIcon label="" size="medium" />}
                        aria-label={intl.formatMessage({
                            id: "confluence.paid.lp.carousel.previous.label",
                            defaultMessage: "Previous slide"
                        })}
                    />
                    <div
                        role="tablist"
                        aria-label={intl.formatMessage({
                            id: "confluence.paid.lp.carousel.tablist.label",
                            defaultMessage: "Choose a slide to display."
                        })}
                        css={carouselTabListStyle}
                    >
                        {slides.map((slide, index) => {
                            const isVisibleTab = visibleSlideIndex === index;
                            return (
                                <div
                                    aria-controls={getSlideId(index)}
                                    aria-selected={isVisibleTab}
                                    key={`${slide.primaryText}-${index}`}
                                    role="tab"
                                    aria-label={intl.formatMessage(
                                        {
                                            id: "confluence.paid.lp.carousel.tab.label",
                                            defaultMessage: `Slide {slideNumber}`
                                        },
                                        { slideNumber: `${index + 1}` }
                                    )}
                                    tabIndex={isVisibleTab ? 0 : -1}
                                    onKeyDown={handleKeyDown}
                                    onClick={() => handleTabClick(index)}
                                    css={[
                                        dotStyle,
                                        isVisibleTab && currentDotStyle
                                    ]}
                                    ref={(node) => {
                                        if (node) {
                                            tabsRef.current[index] = node;
                                        }
                                    }}
                                />
                            );
                        })}
                    </div>
                    <ArrowButton
                        onClick={handleNextClick}
                        aria-label={intl.formatMessage({
                            id: "confluence.paid.lp.carousel.next.label",
                            defaultMessage: "Next slide"
                        })}
                        icon={<ChevronRightLargeIcon label="" size="medium" />}
                    />
                </div>
                <div css={rightAlignedControlsContainerStyles}>
                    <PlayPauseButton
                        onClick={() => setIsRotating(!isRotating)}
                        isRotating={isRotating}
                    />
                </div>
            </div>
        </section>
    );
};
